import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { environmentService } from '../config/environmentService';
import { ApiError } from '../../models/types';

class HttpService {
  private instance: AxiosInstance;
  private retryCount = 0;

  constructor() {
    this.instance = axios.create({
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.instance.interceptors.request.use(
      async (config) => {
        // Add auth token if available
        const token = sessionStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add environment config
        const envConfig = environmentService.getConfig();
        config.baseURL = envConfig.apiBaseUrl;
        config.timeout = envConfig.timeout;
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
        return config;
      },
      (error) => {
        console.log('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(`API Response: ${response.status} ${response.config.url}`, response.data);
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        const envConfig = environmentService.getConfig();

        // Handle 401 Unauthorized - redirect to login
        if (error.response?.status === 401) {
          sessionStorage.removeItem('authToken');
          window.location.href = '/login';
        }

        // Handle retry logic
        try {
          if (this.retryCount < (envConfig.retryAttempts || 3) && this.shouldRetry(error)) {
            this.retryCount++;
            const delay = Math.pow(2, this.retryCount) * 1000; // Exponential backoff

            await new Promise(resolve => setTimeout(resolve, delay));
            return this.instance(originalRequest);
          }
        } catch (configError) {
          // If config is not loaded, use default retry logic
          if (this.retryCount < 3 && this.shouldRetry(error)) {
            this.retryCount++;
            const delay = Math.pow(2, this.retryCount) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
            return this.instance(originalRequest);
          }
        }

        this.retryCount = 0;
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private shouldRetry(error: AxiosError): boolean {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  }

  private handleError(error: AxiosError): ApiError {
    let apiError: ApiError;

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      apiError = {
        code: `HTTP_${status}`,
        message: this.getErrorMessage(status, data),
        details: data,
        timestamp: new Date().toISOString()
      };
    } else if (error.request) {
      // Network error
      apiError = {
        code: 'NETWORK_ERROR',
        message: 'Network error occurred. Please check your connection.',
        details: error.request,
        timestamp: new Date().toISOString()
      };
    } else {
      // Other error
      apiError = {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unknown error occurred',
        details: error,
        timestamp: new Date().toISOString()
      };
    }
    return apiError;
  }

  private getErrorMessage(status: number, data: any): string {
    if (data?.message) {
      return data.message;
    }

    const statusMessages: { [key: number]: string } = {
      400: 'Bad Request - Invalid data provided',
      401: 'Unauthorized - Please login again',
      403: 'Forbidden - You don\'t have permission to access this resource',
      404: 'Not Found - The requested resource was not found',
      409: 'Conflict - The resource already exists',
      422: 'Validation Error - Please check your input',
      429: 'Too Many Requests - Please try again later',
      500: 'Internal Server Error - Please try again later',
      502: 'Bad Gateway - Service temporarily unavailable',
      503: 'Service Unavailable - Please try again later',
      504: 'Gateway Timeout - Request timed out'
    };

    return statusMessages[status] || `HTTP ${status} - An error occurred`;
  }

  // HTTP Methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }

  // File upload
  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.instance.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Download file
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.instance.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

export const httpService = new HttpService(); 